import { lazy, type ComponentType } from 'react';

/*
  The legal, team and CityLab pages, loaded on demand.

  Their HTML arrives as a static English snapshot (scripts/prerender.mjs) that a
  crawler can read without running anything. They are not hydrated: they pick
  their language in the browser and the CityLab shuffles its photos, so the
  snapshot could never match what React renders. main.tsx loads the page's code
  first and only then lets React replace the snapshot, and these lazies resolve
  in the same render once that code is here, so the "CitySeek…" fallback never
  flashes up between the snapshot and the page.
*/
type PageModule = { default: ComponentType<any> };

function preloadable(load: () => Promise<PageModule>) {
  let loaded: PageModule | undefined;
  const preload = () => load().then((module) => (loaded = module));
  // React.lazy reads a thenable that answers immediately as already resolved.
  const Page = lazy(() => (loaded
    ? ({ then: (resolve: (module: PageModule) => void) => resolve(loaded!) } as unknown as Promise<PageModule>)
    : preload()));
  return Object.assign(Page, { preload });
}

export const Impressum = preloadable(() => import('./Impressum'));
export const Privacy = preloadable(() => import('./Privacy'));
export const Terms = preloadable(() => import('./Terms'));
export const Cookies = preloadable(() => import('./Cookies'));
export const TeamMemberProfile = preloadable(() => import('./TeamMemberProfile'));
export const CitySeekCityLab = preloadable(() => import('./CitySeekCityLab'));

const byPath: Record<string, { preload: () => Promise<unknown> }> = {
  '/impressum': Impressum,
  '/privacy': Privacy,
  '/terms': Terms,
  '/cookies': Cookies,
  '/cityseek_citylab': CitySeekCityLab,
  '/cityseek_popup_meetup': CitySeekCityLab,
  '/team/safa-kaskaya': TeamMemberProfile,
  '/team/umut-bilgic': TeamMemberProfile,
};

/** Load the code of the page at this address, if it is one of these. */
export function preloadPage(pathname: string): Promise<unknown> {
  const page = byPath[pathname.replace(/\/+$/, '').toLowerCase()];
  return page ? page.preload() : Promise.resolve();
}
